<template>
  <l-table
    ref="LTable"
    :module-name="MODULE_NAME"
    :table-columns="tableColumns"
    :filter-class="null"
  >
    <template #create-button>
      <b-button
        v-if="canCreateRequestLetter"
        v-b-toggle.sidebar-create-request-letter
        variant="primary"
        @click="created"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-50"
        />
        <span>{{ $t('Create') }}</span>
      </b-button>
    </template>

    <template #before-search>
      <v-select
        v-model="status"
        :options="statusOptions"
        :reduce="option => option.id"
        :get-option-label="option => $langName(option.name)"
        :placeholder="$t('Status')"
        class="request-letter-status-filter mr-1"
        @input="filterByStatus"
      />
    </template>

    <template #create-body>
      <create
        ref="createRequestLetter"
        @refetchData="refetchData"
      />
      <review
        :request-letter="selectedRequestLetter"
        @refetchData="refetchData"
      />
    </template>

    <template #cell(org)="{ data }">
      {{ $langName(data.value && data.value.name) }}
    </template>

    <template #cell(status)="{ data }">
      <b-badge :variant="getStatus(data.value).variant">
        {{ $langName(getStatus(data.value).name) }}
      </b-badge>
    </template>

    <template #cell(comment)="{ data }">
      {{ data.value || '-' }}
    </template>

    <template #cell(created_at)="{ data }">
      {{ formatDate(data.value) }}
    </template>

    <template #cell(actions)="{ data }">
      <div class="text-nowrap">
        <feather-icon
          icon="EyeIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="$router.push({ name: 'request-letters-view', params: { id: data.item.id } })"
        />
        <feather-icon
          v-if="canUpdate(data.item)"
          v-b-toggle.sidebar-create-request-letter
          icon="EditIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="update(data.item)"
        />
        <feather-icon
          v-if="canSend(data.item)"
          icon="SendIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="send(data.item)"
        />
        <feather-icon
          v-if="canReview(data.item)"
          v-b-toggle.sidebar-review-request-letter
          icon="CheckCircleIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="selectedRequestLetter = data.item"
        />
        <feather-icon
          v-if="canDelete(data.item)"
          icon="TrashIcon"
          class="cursor-pointer mr-1"
          size="16"
          @click="remove(data.item)"
        />
      </div>
    </template>
  </l-table>
</template>

<script>
import {
  BButton, BBadge, VBToggle,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import requestLettersModule from '@/store/request-letters'
import { isRoleUniversity } from '@/auth/utils'
import LTable from '@/views/components/LTable.vue'
import Create from './Create.vue'
import Review from './Review.vue'
import config, { getRequestLetterStatus, REQUEST_LETTER_STATUSES } from './config'
import permission from './permission'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BButton,
    BBadge,
    LTable,
    Create,
    Review,
    vSelect,
  },
  data() {
    return {
      status: null,
      selectedRequestLetter: {},
    }
  },
  computed: {
    statusOptions() {
      return Object.values(REQUEST_LETTER_STATUSES).map(id => ({
        id,
        ...getRequestLetterStatus(id),
      }))
    },
    canCreateRequestLetter() {
      return isRoleUniversity()
    },
  },
  created() {
    this.$store.commit(`${this.MODULE_NAME}/FRESH`)
  },
  methods: {
    getStatus(status) {
      return getRequestLetterStatus(status)
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString()
    },
    filterByStatus() {
      if (this.status) {
        this.$refs.LTable.extraParams.status = this.status
      } else {
        delete this.$refs.LTable.extraParams.status
      }
      this.refetchData()
    },
    created() {
      this.$store.commit(`${this.MODULE_NAME}/FRESH`)
    },
    update(data) {
      this.$store.commit(`${this.MODULE_NAME}/UPDATE`, {
        ...data,
        attachment_file: null,
        apply_docs: (data.apply_docs || []).map(item => item.id || item),
      })
    },
    send(data) {
      this.$removeSwal(this, data.id, `${this.MODULE_NAME}/send`, this.refetchData, {
        text: '',
        confirmButtonText: 'Send',
        successTitle: 'Sent',
        successText: 'Request sent successfully',
        errorText: 'Failed to send request',
      })
    },
    remove(data) {
      this.$removeSwal(this, data, `${this.MODULE_NAME}/del`, this.refetchData)
    },
    refetchData() {
      this.$refs.LTable.refetchData()
    },
  },
  setup() {
    const MODULE_NAME = 'requestLetters'
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, requestLettersModule)

    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    return {
      MODULE_NAME,
      ...config(),
      ...permission(),
    }
  },
}
</script>

<style scoped>
.request-letter-status-filter {
  min-width: 180px;
}
</style>
